import { AppShell, PageTitle } from "@/components/app-shell";
import { ProjectMaterialsPanel } from "@/components/project-materials";
import { MindCanvasPanel } from "@/pages/mind";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { requestOpenThink } from "@/lib/underline-nav";
import { BookOpen, Bot, GitBranch, MessageSquare } from "lucide-react";
import { Link, useParams } from "react-router-dom";

/**
 * Agents tab — Think dock, Circuit Mind canvas, Review-agent findings.
 * There is no multi-session agent hub (no session list, no in-page Circuit Mind chat).
 */
export function AgentsPage() {
  const { orgSlug, projectSlug } = useParams();
  const base = orgSlug && projectSlug ? `/o/${orgSlug}/p/${projectSlug}` : "";

  return (
    <AppShell
      title={
        <PageTitle kicker="Think · Circuit Mind · Review agent">
          Agents
        </PageTitle>
      }
    >
      <div
        className="mb-5 rounded-md border border-border bg-card px-4 py-3"
        data-testid="agents-honest-empty"
      >
        <p className="m-0 text-[13px] font-medium text-foreground">No agent-session hub yet</p>
        <p className="mt-1 mb-0 max-w-[640px] text-[13px] text-muted-foreground">
          This tab is not a GitHub-style Agents product. There is no session list, no persisted
          Circuit Mind / Review-agent chats, and no in-page runner. The surfaces below are the ones
          that actually exist in this workbench.
        </p>
      </div>

      <div className="mb-6 grid gap-3 md:grid-cols-3">
        <article className="rounded-md border border-border bg-card p-4">
          <div className="mb-2 flex items-center gap-2 text-[13px] font-medium">
            <MessageSquare className="size-4 text-[var(--copper)]" aria-hidden />
            Think
            <Badge variant="outline">dock</Badge>
          </div>
          <p className="mb-3 text-[13px] text-muted-foreground">
            Live chat is the Think rail on the right (TH). Opening it here expands that dock — it is
            not a separate agent workspace.
          </p>
          <Button size="sm" variant="outline" onClick={() => requestOpenThink()}>
            Open Think dock
          </Button>
        </article>

        <article className="rounded-md border border-border bg-card p-4">
          <div className="mb-2 flex items-center gap-2 text-[13px] font-medium">
            <Bot className="size-4 text-[var(--copper)]" aria-hidden />
            Circuit Mind
            <Badge variant="outline">canvas</Badge>
          </div>
          <p className="mb-3 text-[13px] text-muted-foreground">
            Read-only architecture projection on the canvas below. Not a chat agent. Hardware SoT
            stays KiCad on Files.
          </p>
        </article>

        <article className="rounded-md border border-border bg-card p-4">
          <div className="mb-2 flex items-center gap-2 text-[13px] font-medium">
            <GitBranch className="size-4 text-[var(--copper)]" aria-hidden />
            Review agent
            <Badge variant="outline">on Changes</Badge>
          </div>
          <p className="mb-3 text-[13px] text-muted-foreground">
            Review-scoped findings run on a Design Review. There is no standalone Review-agent
            session list or chat.
          </p>
          <Button size="sm" variant="outline" asChild>
            <Link to={`${base}/changes`}>Open Changes</Link>
          </Button>
        </article>
      </div>

      <section
        className="mb-6 rounded-md border border-border bg-card p-4"
        data-testid="agents-project-library"
      >
        <div className="mb-2 flex items-center gap-2 text-[13px] font-medium">
          <BookOpen className="size-4 text-[var(--copper)]" aria-hidden />
          Project library
          <Badge variant="outline">materials</Badge>
        </div>
        <p className="mb-3 text-[13px] text-muted-foreground">
          Datasheets, papers, notes, and BOM snapshots for this project. Upload here for future
          indexing; the dock agent uses built-in read/list on the workspace snapshot (AI Search search
          is temporarily off).
        </p>
        <ProjectMaterialsPanel compact />
      </section>

      <h2 className="mb-2 text-[15px] font-semibold tracking-[0.02em] text-foreground">Circuit Mind canvas</h2>
      <p className="mt-0 mb-3 text-[13px] text-muted-foreground">
        Live architecture projection from Think (mind_scene / cached scene). Honest empty if this project has no
        scene yet — never a silent demo.
      </p>
      <MindCanvasPanel showEmptyCtaCard />
    </AppShell>
  );
}
